/**
 * Um ábaco de classificação com os pontos da vertical (spec 07 §6.1, item 4; onda 313, R5, R6, R7, R10).
 *
 * A moldura, os eixos, as linhas e os rótulos vêm de `desenhoDoAbaco` (o motor: só as linhas que
 * classificam); os pontos, de `pontosDoAbaco` — um `path` por zona, na cor do token da zona (`var(--ts-…)`),
 * e o que passa da moldura na borda, contado embaixo. Com `destacados` (o trecho selecionado nas trilhas),
 * esses pontos crescem com a borda laranja e os outros esmaecem; o arrasto de um retângulo devolve os
 * índices de r (`aoSelecionar`) para as trilhas acenderem as mesmas leituras. Numa simulação, o antes
 * aparece em cinza vazado por baixo.
 *
 * A figura diz, em `data-pontos` e `data-destacados`, quantas leituras estão desenhadas e quantas delas
 * estão destacadas — as leituras, não os quadradinhos (um por pixel): a tela e o teste contam por aí.
 *
 * O Schneider leva `FONTE_SCHNEIDER` e a marca de sem gabarito; o Qt1 × Bq, a frase de que não há
 * fronteiras. Nos dois a cor é a zona SBTn da leitura — e a marca do ábaco (`marcaDoAbaco`) soma a do SBTn
 * à dos eixos. A legenda das cores (`LegendaSbt`, com as zonas presentes) vai junto: a cor nunca sozinha.
 */
import type { JSX } from 'react';
import { useMemo, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { ZONAS_SBTN, ZONAS_SBTN_MOD, ZONAS_SCHNEIDER } from '../motor';
import type { Resultado } from '../motor';
import { desenhoDoAbaco, fraseDaBorda, indicesNoRetangulo, leituras, marcaDoAbaco, pontosDoAbaco } from './abacos';
import type { TipoDeAbaco } from './abacos';
import { dominioDe, escala, marcas, numeroBr } from './escalas';
import type { Escala, EscalaDoEixo } from './escalas';
import { MARCA_NAO_HOMOLOGADA } from './homologacao';
import { LegendaSbt } from './LegendaSbt';
import { useLargura } from './useLargura';

/** As margens do desenho, em pixel. */
const M = { esq: 52, dir: 14, topo: 10, baixo: 42 };
const ARRASTO_MINIMO = 4;

/** O título da legenda das cores de cada ábaco (no Schneider e no Qt1 × Bq a cor é a zona SBTn). */
const TITULO_DA_LEGENDA: Readonly<Record<TipoDeAbaco, string | undefined>> = {
  sbtn: undefined,
  sbt: 'Zonas SBT (Robertson 2010)',
  sbtnMod: undefined,
  schneider: 'A cor dos pontos: zonas SBTn (Qtn × Fr)',
  qtBq: 'A cor dos pontos: zonas SBTn (Qtn × Fr)',
};

/** O rótulo de uma marca do eixo. */
function rotulo(v: number, todas: readonly number[], tipo: EscalaDoEixo): string {
  if (tipo === 'log') return numeroBr(v, v >= 1 ? 0 : Math.ceil(-Math.log10(v) - 1e-9));
  const passo = todas.length > 1 ? Math.abs(todas[1] - todas[0]) : 1;
  return numeroBr(v, Math.max(0, Math.ceil(-Math.log10(passo) - 1e-9)));
}

/** As linhas menores de um eixo log (2 a 9 de cada década). */
function menores(dominio: [number, number]): number[] {
  const saida: number[] = [];
  for (let e = Math.floor(Math.log10(dominio[0])); e < Math.ceil(Math.log10(dominio[1])); e++) {
    for (let m = 2; m <= 9; m++) {
      const v = m * 10 ** e;
      if (v > dominio[0] && v < dominio[1]) saida.push(v);
    }
  }
  return saida;
}

/** Os pontos de uma lista de índices como quadradinhos num `d` (um por pixel). */
function quadrados(indices: readonly number[], x: Float64Array, y: Float64Array, ex: Escala, ey: Escala, lado: number): string {
  const vistos = new Set<string>();
  const partes: string[] = [];
  const meio = lado / 2;
  for (const i of indices) {
    const px = ex.para(x[i]);
    const py = ey.para(y[i]);
    if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
    const chave = `${Math.round(px)},${Math.round(py)}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    partes.push(`M${(px - meio).toFixed(1)} ${(py - meio).toFixed(1)}h${lado}v${lado}h${-lado}z`);
  }
  return partes.join('');
}

/** O token da cor de uma zona, ou null (sem classe). */
function tokenDaZona(tipo: TipoDeAbaco, zona: number): string | null {
  const zonasDoTipo = (tipo === 'sbtnMod' ? ZONAS_SBTN_MOD : ZONAS_SBTN) as Readonly<Record<number, { token: string }>>;
  return zonasDoTipo[zona]?.token ?? null;
}

/** Um ábaco com os pontos de uma vertical. */
export function Abaco(p: {
  tipo: TipoDeAbaco;
  r: Resultado;
  antes?: Resultado | null;
  destacados?: Uint8Array | null;
  aoSelecionar?(indices: number[] | null): void;
}): JSX.Element {
  const { tipo, r, antes } = p;
  const [ref, largura] = useLargura<HTMLElement>();
  const desenho = desenhoDoAbaco(tipo);
  const pontos = useMemo(() => pontosDoAbaco(tipo, r), [tipo, r]);
  const pontosAntes = useMemo(() => (antes ? pontosDoAbaco(tipo, antes) : null), [tipo, antes]);

  const lado = Math.max(200, Math.min(440, largura - M.esq - M.dir));
  const larguraSvg = M.esq + lado + M.dir;
  const alturaSvg = M.topo + lado + M.baixo;
  const dominioX = useMemo((): [number, number] => {
    if (desenho.eixoX.dominio !== 'dados') return desenho.eixoX.dominio;
    const [a, b] = dominioDe([pontos.x, ...(pontosAntes ? [pontosAntes.x] : [])], 'linear', { comZero: true });
    const folga = (b - a) * 0.05;
    return [a - folga, b + folga];
  }, [desenho, pontos, pontosAntes]);
  const ex = escala(desenho.eixoX.escala, dominioX, [M.esq, M.esq + lado]);
  const ey = escala(desenho.eixoY.escala, desenho.eixoY.dominio, [M.topo + lado, M.topo]);

  // Os pontos, um path por zona; com destacados, os de fora esmaecem e os de dentro crescem.
  const desenhoDosPontos = useMemo(() => {
    const porZona = new Map<number, number[]>();
    const destacadosPorZona = new Map<number, number[]>();
    for (let i = 0; i < pontos.x.length; i++) {
      const alvo = p.destacados && p.destacados[i] ? destacadosPorZona : porZona;
      const z = pontos.zona[i];
      const lista = alvo.get(z);
      if (lista) lista.push(i);
      else alvo.set(z, [i]);
    }
    const porZonaEmOrdem = (m: Map<number, number[]>) => [...m.entries()].sort((a, b) => a[0] - b[0]);
    return {
      comuns: porZonaEmOrdem(porZona).map(([z, ind]) => ({ z, d: quadrados(ind, pontos.x, pontos.y, ex, ey, 3) })),
      destacados: porZonaEmOrdem(destacadosPorZona).map(([z, ind]) => ({ z, d: quadrados(ind, pontos.x, pontos.y, ex, ey, 6) })),
      antes: pontosAntes
        ? quadrados(Array.from({ length: pontosAntes.x.length }, (_, i) => i), pontosAntes.x, pontosAntes.y, ex, ey, 3)
        : '',
    };
  }, [pontos, pontosAntes, p.destacados, ex, ey]);
  const algumDestacado = desenhoDosPontos.destacados.length > 0;

  // O retângulo da seleção.
  const inicio = useRef<{ x: number; y: number } | null>(null);
  const [retangulo, setRetangulo] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const noSvg = (e: PointerEvent<SVGRectElement>): { x: number; y: number } => {
    const caixa = (e.currentTarget.ownerSVGElement ?? e.currentTarget).getBoundingClientRect();
    return { x: e.clientX - caixa.left, y: e.clientY - caixa.top };
  };

  const marcasX = marcas(desenho.eixoX.escala, dominioX, 6);
  const marcasY = marcas(desenho.eixoY.escala, desenho.eixoY.dominio, 6);
  const marca = marcaDoAbaco(tipo, r.premissas);
  // As zonas com ponto no desenho (a legenda diz quais não ocorrem).
  const presentes = useMemo(() => {
    const z = new Set<number>();
    for (let i = 0; i < pontos.x.length; i++) if (Number.isFinite(pontos.x[i]) && pontos.zona[i] > 0) z.add(pontos.zona[i]);
    return z;
  }, [pontos]);
  const desenhados = useMemo(() => {
    let n = 0;
    for (let i = 0; i < pontos.x.length; i++) if (Number.isFinite(pontos.x[i]) && Number.isFinite(pontos.y[i])) n++;
    return n;
  }, [pontos]);
  const nDestacados = useMemo(() => {
    let n = 0;
    if (p.destacados) {
      for (let i = 0; i < pontos.x.length; i++) {
        if (p.destacados[i] && Number.isFinite(pontos.x[i]) && Number.isFinite(pontos.y[i])) n++;
      }
    }
    return n;
  }, [pontos, p.destacados]);
  const borda = fraseDaBorda(tipo, r);
  const caminho = (pts: readonly { x: number; y: number }[]): string =>
    pts.map((q, k) => `${k === 0 ? 'M' : 'L'}${ex.para(q.x).toFixed(1)} ${ey.para(q.y).toFixed(1)}`).join('');

  return (
    <figure ref={ref} className="cptu-abaco" data-pontos={desenhados} data-destacados={nDestacados}>
      <figcaption className="cptu-abaco-titulo">
        <span className="cptu-titulo-html">{desenho.titulo}</span>
        {!marca.homologada ? (
          <span className="cptu-marca" title={marca.motivo ?? undefined}>
            {' '}{MARCA_NAO_HOMOLOGADA}{marca.motivo ? <span className="cptu-nota"> — {marca.motivo}</span> : null}
          </span>
        ) : null}
      </figcaption>
      {largura > 0 ? (
        <svg width={larguraSvg} height={alturaSvg} role="img" aria-label={`${desenho.titulo}: ${leituras(desenhados)}`}>
          <rect className="cptu-moldura" x={M.esq} y={M.topo} width={lado} height={lado} />
          {desenho.eixoX.escala === 'log' ? menores(dominioX).map((v) => (
            <line key={`mx${v}`} className="cptu-grade menor" x1={ex.para(v)} x2={ex.para(v)} y1={M.topo} y2={M.topo + lado} />
          )) : null}
          {menores(desenho.eixoY.dominio).map((v) => (
            <line key={`my${v}`} className="cptu-grade menor" x1={M.esq} x2={M.esq + lado} y1={ey.para(v)} y2={ey.para(v)} />
          ))}
          {marcasX.map((v) => (
            <g key={`x${v}`}>
              <line className="cptu-grade" x1={ex.para(v)} x2={ex.para(v)} y1={M.topo} y2={M.topo + lado} />
              <text className="cptu-texto cptu-texto-2" x={ex.para(v)} y={M.topo + lado + 16} textAnchor="middle">
                {rotulo(v, marcasX, desenho.eixoX.escala)}
              </text>
            </g>
          ))}
          {marcasY.map((v) => (
            <g key={`y${v}`}>
              <line className="cptu-grade" x1={M.esq} x2={M.esq + lado} y1={ey.para(v)} y2={ey.para(v)} />
              <text className="cptu-texto cptu-texto-2" x={M.esq - 6} y={ey.para(v) + 4} textAnchor="end">
                {rotulo(v, marcasY, desenho.eixoY.escala)}
              </text>
            </g>
          ))}
          {desenho.eixoX.escala === 'linear' && dominioX[0] < 0 && dominioX[1] > 0 ? (
            <line className="cptu-zero" x1={ex.para(0)} x2={ex.para(0)} y1={M.topo} y2={M.topo + lado} />
          ) : null}
          <text className="cptu-texto" x={M.esq + lado / 2} y={M.topo + lado + 34} textAnchor="middle">{desenho.eixoX.rotulo}</text>
          <text className="cptu-texto" transform={`translate(14 ${M.topo + lado / 2}) rotate(-90)`} textAnchor="middle">
            {desenho.eixoY.rotulo}
          </text>

          <svg x={M.esq} y={M.topo} width={lado} height={lado} overflow="hidden">
            <g transform={`translate(${-M.esq} ${-M.topo})`}>
              {desenhoDosPontos.antes ? <path className="cptu-antes-ponto" d={desenhoDosPontos.antes} /> : null}
              {desenhoDosPontos.comuns.map(({ z, d }) => {
                const token = tokenDaZona(tipo, z);
                return (
                  <path
                    key={`p${z}`}
                    className={`cptu-pontos${token ? '' : ' sem-zona'}${algumDestacado ? ' esmaecido' : ''}`}
                    d={d}
                    fill={token ? `var(${token})` : undefined}
                  />
                );
              })}
              {desenho.linhas.map((l) => <path key={l.chave} className="cptu-fronteira" d={caminho(l.pontos)} />)}
              {desenhoDosPontos.destacados.map(({ z, d }) => {
                const token = tokenDaZona(tipo, z);
                return (
                  <path key={`d${z}`} className={`cptu-pontos destacado${token ? '' : ' sem-zona'}`} d={d}
                    fill={token ? `var(${token})` : undefined} />
                );
              })}
              {desenho.rotulos.map((rot) => (
                <text key={rot.zona} className="cptu-rotulo-zona" x={ex.para(rot.x)} y={ey.para(rot.y) + 4} textAnchor="middle">
                  {rot.texto}
                </text>
              ))}
            </g>
          </svg>
          {retangulo ? (
            <rect
              className="cptu-selecao"
              x={Math.min(retangulo.x0, retangulo.x1)}
              y={Math.min(retangulo.y0, retangulo.y1)}
              width={Math.abs(retangulo.x1 - retangulo.x0)}
              height={Math.abs(retangulo.y1 - retangulo.y0)}
            />
          ) : null}
          {p.aoSelecionar ? (
            <rect
              className="cptu-captura cptu-captura-do-abaco"
              x={M.esq}
              y={M.topo}
              width={lado}
              height={lado}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.currentTarget.setPointerCapture?.(e.pointerId);
                inicio.current = noSvg(e);
              }}
              onPointerMove={(e) => {
                const a = inicio.current;
                if (!a) return;
                const b = noSvg(e);
                if (Math.hypot(b.x - a.x, b.y - a.y) < ARRASTO_MINIMO && !retangulo) return;
                setRetangulo({ x0: a.x, y0: a.y, x1: b.x, y1: b.y });
              }}
              onPointerUp={(e) => {
                const a = inicio.current;
                inicio.current = null;
                setRetangulo(null);
                if (!a) return;
                const b = noSvg(e);
                if (Math.hypot(b.x - a.x, b.y - a.y) < ARRASTO_MINIMO) {
                  p.aoSelecionar?.(null);
                  return;
                }
                p.aoSelecionar?.(indicesNoRetangulo(pontos, { x0: ex.de(a.x), x1: ex.de(b.x), y0: ey.de(a.y), y1: ey.de(b.y) }));
              }}
              onPointerCancel={() => {
                inicio.current = null;
                setRetangulo(null);
              }}
            />
          ) : null}
        </svg>
      ) : null}
      <div className="cptu-abaco-notas">
        {borda ? <p className="cptu-nota">{borda}</p> : null}
        {desenho.fonte ? (
          <p className="cptu-nota">
            {tipo === 'schneider' ? `Fronteiras: ${desenho.fonte}.` : `${desenho.fonte.charAt(0).toUpperCase()}${desenho.fonte.slice(1)}.`}
          </p>
        ) : null}
        {tipo === 'schneider' ? (
          <ul className="cptu-regioes">
            {Object.values(ZONAS_SCHNEIDER).map((z) => <li key={z.codigo}><b>{z.codigo}</b> · {z.nome}</li>)}
          </ul>
        ) : null}
      </div>
      <LegendaSbt tipo={tipo === 'sbtnMod' ? 'sbtnMod' : 'sbtn'} presentes={presentes} titulo={TITULO_DA_LEGENDA[tipo]} />
    </figure>
  );
}
